import { memo } from 'react'
import { Helmet } from 'react-helmet'
import { HOSTNAME } from '@constants'

type SEOProps = {
  title?: string
  description?: string
  image?: string
  imageWidth?: number
  imageHeight?: number
  type?: string
  schema?: string
  published?: string
  updated?: string
  tags?: string
  robots?: string
  noIndex?: boolean
  children?: JSX.Element | JSX.Element[]
}

const SEO = ({
  title,
  description,
  image,
  imageWidth,
  imageHeight,
  type,
  schema,
  published,
  updated,
  tags,
  robots,
  noIndex,
  children,
}: SEOProps): JSX.Element => {
  return (
    <Helmet>
      <html itemScope itemType={`http://schema.org/${String(schema)}`} lang="en-US" />

      <title>{title}</title>
      <meta name="description" content={description} />

      <meta itemProp="name" content={title} />
      <meta itemProp="description" content={description} />
      <meta itemProp="image" content={image} />

      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:site" content="@tracklib" />
      <meta name="twitter:card" content="summary_large_image" />

      <meta property="og:title" content={title} />
      {/* Url meta tag is added by server. see server.js */}
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      {imageWidth && <meta property="og:image:width" content={String(imageWidth)} />}
      {imageHeight && <meta property="og:image:height" content={String(imageHeight)} />}
      <meta property="og:type" content={type} />

      {published && <meta property="article:published_time" content={published} />}
      {updated && <meta property="article:modified_time" content={updated} />}
      {tags && <meta property="article:tag" content={tags} />}

      {robots && <meta name="robots" content={robots} />}

      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {children}
    </Helmet>
  )
}

SEO.defaultProps = {
  title: 'Tracklib - the record store for sampling',
  description:
    'Tracklib is the only service where anyone can legally clear samples from real music. From Isaac Hayes to Mozart, we got it.',
  image: `${HOSTNAME}/static/images/og-image.jpg`,
  type: 'website',
  schema: 'WebSite',
}

export default memo<SEOProps>(SEO)
